import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect, DispatchProp } from 'react-redux';
import { Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import _ from 'lodash';
import {
  ArrayParam,
  DateParam,
  NumberParam,
  useQueryParams,
} from 'use-query-params';

import { ConversationsFilters } from 'src/api/api';
import Color from 'src/assets/_util.scss';
import CollectionFilter, {
  getActiveCollections,
  handleCollectionChange,
} from 'src/components/card-filters/CollectionFilter';
import DateFilter, {
  getActiveEndDates,
  getActiveStartDates,
} from 'src/components/card-filters/DateFilter';
import FilterEmptyState from 'src/components/card-filters/FilterEmptyState';
import ForumFilter, {
  getActiveForums,
  handleForumChange,
} from 'src/components/card-filters/ForumFilter';
import LanguageFilter, {
  getActiveLanguages,
  handleLanguageChange,
} from 'src/components/card-filters/LanguageFilter';
import PartnerFilter, {
  getActiveOrganizations,
  handleOrganizationChange,
} from 'src/components/card-filters/PartnerFilter';
import PrivacyFilter, {
  getActivePrivacy,
  handlePrivacyChange,
} from 'src/components/card-filters/PrivacyFilter';
import ConversationCard from 'src/components/ConversationCard/ConversationCard';
import LoadingSpinner from 'src/components/core/LoadingSpinner/LoadingSpinner';
import { VStack } from 'src/components/core/Stack';
import FilterTags from 'src/components/FilterTags/FilterTags';
import PagingControl from 'src/components/PagingControl/PagingControl';
import PagingSummary from 'src/components/PagingSummary/PagingSummary';
import authSelectors from 'src/redux/auth/auth-selectors';
import conversationsSelectors from 'src/redux/conversations/conversations-selectors';
import { loadConversations } from 'src/redux/conversations/conversations-slice';
import { StoreState } from 'src/redux/store';
import { User } from 'src/types/auth';
import { Conversation, Paging } from 'src/types/conversation';
import { ConversationsFilterOptions } from 'src/types/core';
import dateFormatter from 'src/util/date';
import { makePagingHandlers } from 'src/util/paging';
import ClearableTag from '../ClearableTag/ClearableTag';

import styles from './ConversationCards.module.scss';

interface StateProps {
  isLoading: boolean;
  conversations: Conversation[];
  error: Error | undefined;
  pagingInfo: Paging | undefined;
  filterOptions: ConversationsFilterOptions | undefined;
  user: User;
}

type Props = StateProps & DispatchProp;

const mapStateToProps = (state: StoreState): StateProps => ({
  isLoading: conversationsSelectors.isLoading(state),
  conversations: conversationsSelectors.getConversations(state),
  error: conversationsSelectors.getError(state),
  pagingInfo: conversationsSelectors.getConversationsPagingInfo(state),
  filterOptions: conversationsSelectors.getConversationsFilterOptions(state),
  user: authSelectors.getUser(state),
});

const PAGE_SIZE = 10;

const ConversationCards = ({
  conversations,
  isLoading = true,
  error,
  pagingInfo,
  filterOptions,
  user,
  dispatch,
}: Props) => {
  const { t } = useTranslation();
  // get search query parameters
  const [query, setQuery] = useQueryParams({
    c: ArrayParam,
    f: ArrayParam,
    page: NumberParam,
    start: DateParam,
    end: DateParam,
    org: ArrayParam,
    lang: ArrayParam,
    priv: ArrayParam,
  });
  const {
    c: collectionIds,
    f: forumIds,
    page: currentPage = 1,
    start: startDate,
    end: endDate,
    org: organizationIds,
    lang: languageCodes,
    priv: privacyLevels,
  } = query;

  const [draftStartDate, setDraftStartDate] = React.useState('');
  const [draftEndDate, setDraftEndDate] = React.useState('');
  const [mounted, setMounted] = React.useState(false);
  const filters: Partial<ConversationsFilters> = React.useMemo(() => {
    return {
      page: currentPage,
      limit: PAGE_SIZE,
      collectionIds,
      forumIds,
      organizationIds,
      languageCodes,
      privacyLevels,
      startDate,
      endDate,
    };
  }, [
    currentPage,
    collectionIds,
    forumIds,
    organizationIds,
    languageCodes,
    privacyLevels,
    startDate,
    endDate,
  ]);

  React.useEffect(() => {
    // on first load, if no orgs are selected, set the orgs to the user's orgs
    const initialFilters: Partial<ConversationsFilters> = { ...filters };
    if (!mounted) {
      if (!initialFilters.organizationIds?.length) {
        const myOrgs = user.organizations.map((o) => o.id.toString());
        setQuery({ org: myOrgs }, 'replaceIn');
        initialFilters.organizationIds = myOrgs;
      }
      if (startDate) {
        setDraftStartDate(dateFormatter.dateToISOString(startDate));
      }
      if (endDate) {
        setDraftEndDate(dateFormatter.dateToISOString(endDate));
      }
    }
    dispatch(loadConversations(initialFilters));
    window.scrollTo(0, 0);
    setMounted(true);
  }, []);

  //load converstaions when filters change
  React.useEffect(() => {
    if (mounted) {
      dispatch(loadConversations(filters));
    }
  }, [filters, dispatch, mounted]);

  const clearAllFilters = () => {
    setDraftStartDate('');
    setDraftEndDate('');
    setQuery({
      org: undefined,
      lang: undefined,
      start: undefined,
      end: undefined,
      c: undefined,
      f: undefined,
      priv: undefined,
    });
  };

  const { handleNextPage, handlePrevPage, handleSeekPage } = makePagingHandlers(
    currentPage,
    PAGE_SIZE,
    pagingInfo ? pagingInfo.total : 0,
    (page: number) => setQuery({ page }, 'pushIn')
  );

  const activeOrgs = getActiveOrganizations(organizationIds, filterOptions);
  const activeCollections = getActiveCollections(collectionIds, filterOptions);
  const activeForums = getActiveForums(forumIds, filterOptions);
  const activeLanguages = getActiveLanguages(languageCodes, filterOptions);
  const activePrivacy = getActivePrivacy(privacyLevels, filterOptions);
  const activeStartDate = startDate && getActiveStartDates(startDate);
  const activeEndDate = endDate && getActiveEndDates(endDate);

  const filtersActive =
    collectionIds != null ||
    forumIds != null ||
    startDate != null ||
    endDate != null ||
    languageCodes != null ||
    organizationIds != null ||
    privacyLevels != null;

  const hasConversations = conversations.length > 0;
  const ready = !isLoading && hasConversations;

  const activeOrgsIds = _.reject(activeOrgs, _.isUndefined).map((o) =>
    o?.id?.toString()
  ) as string[];

  return (
    <div className="d-flex overflow-hidden">
      {error ? (
        <p className="text-danger">
          {t('common.error_unexpected')}
          {process.env.NODE_ENV !== 'production' && error.message}
        </p>
      ) : (
        <>
          <div style={{ marginRight: '12px' }}>
            <PartnerFilter filterOptions={filterOptions} />
            <CollectionFilter filterOptions={filterOptions} />
            <ForumFilter filterOptions={filterOptions} />
            <LanguageFilter filterOptions={filterOptions} />
            <PrivacyFilter filterOptions={filterOptions} />
            <DateFilter
              draftStartDate={draftStartDate}
              draftEndDate={draftEndDate}
              setDraftStartDate={setDraftStartDate}
              setDraftEndDate={setDraftEndDate}
            />
          </div>

          <VStack className="flex-grow-1">
            <VStack className={styles.conversationCardsContainer}>
              {filtersActive && (
                <div className="pb-2 px-3">
                  <ClearableTag
                    color="danger"
                    iconColor={Color.danger}
                    pale={true}
                    uppercase
                    className="me-1 mb-1"
                    onClear={clearAllFilters}
                    key={'all-filters'}
                    data-testid={`all-filters-tag`}
                    tagName={'all-filters'}
                  >
                    {t('conversations.clear_button')}
                  </ClearableTag>
                  <FilterTags
                    activeFilterOptions={activeOrgs}
                    onClear={(option) =>
                      handleOrganizationChange(option, activeOrgsIds, setQuery)
                    }
                  />
                  <FilterTags
                    activeFilterOptions={activeCollections}
                    onClear={(option) =>
                      handleCollectionChange(option, collectionIds, setQuery)
                    }
                  />
                  <FilterTags
                    activeFilterOptions={activeForums}
                    onClear={(option) =>
                      handleForumChange(option, forumIds, setQuery)
                    }
                  />
                  <FilterTags
                    activeFilterOptions={activeLanguages}
                    onClear={(option) =>
                      handleLanguageChange(option, languageCodes, setQuery)
                    }
                  />
                  <FilterTags
                    activeFilterOptions={activePrivacy}
                    onClear={(option) =>
                      handlePrivacyChange(option, privacyLevels, setQuery)
                    }
                  />
                  {startDate && (
                    <FilterTags
                      activeFilterOptions={activeStartDate}
                      onClear={() => {
                        setDraftStartDate('');
                        setQuery({ start: undefined });
                      }}
                    />
                  )}

                  {endDate && (
                    <FilterTags
                      activeFilterOptions={activeEndDate}
                      onClear={() => {
                        setDraftEndDate('');
                        setQuery({ end: undefined });
                      }}
                    />
                  )}
                </div>
              )}

              {ready && (
                <PagingSummary
                  className="text-gray-700 px-3"
                  itemsPerPage={PAGE_SIZE}
                  pagingInfo={pagingInfo}
                  i18NextKey="conversations.showing"
                  i18NextRangeKey="conversations.showing_range"
                />
              )}

              <VStack sx={{ overflow: 'auto', p: 4 }}>
                {isLoading ? (
                  <div className="w-100 text-center mt-4">
                    <LoadingSpinner active size="lg" />
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <ConversationCard
                      key={conversation.id}
                      className={`mb-3 ${styles.card}`}
                      conversation={conversation}
                    />
                  ))
                )}
              </VStack>

              {ready && (
                <PagingControl
                  className={'mb-5 text-center w-100'}
                  itemsPerPage={PAGE_SIZE}
                  pagingInfo={pagingInfo}
                  onNext={handleNextPage}
                  onPrevious={handlePrevPage}
                  onSeek={handleSeekPage}
                  style={{
                    borderTop: `1px solid ${Color.gray400}`,
                    width: '100%',
                  }}
                />
              )}
            </VStack>

            {!isLoading && !hasConversations && (
              <VStack sx={{ gap: 3, textAlign: 'center' }}>
                <FilterEmptyState
                  filtersActive={filtersActive}
                  onClear={clearAllFilters}
                  title={t('conversations.empty_state')}
                />
                <Typography variant="body1">
                  <Trans
                    i18nKey="conversations.empty_state_subtext"
                    /*eslint-disable-next-line */
                    components={{
                      1: <Link to="/admin?tab=draft_conversations" />,
                    }}
                  />
                </Typography>
              </VStack>
            )}
          </VStack>
        </>
      )}
    </div>
  );
};

export default connect(mapStateToProps)(ConversationCards);
